import { Fragment, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, ChevronUp, CircleAlert, EyeOff, Info, Layers, Lock, PackageSearch, Search, SearchX, ShoppingCart, Truck, WandSparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import RequestPurchaseDialog, { RequestForProjectsDialog } from './RequestPurchaseDialog';
import AutoBookPanel from './AutoBookPanel';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate, formatNumber, formatPercent } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  ChipSelect,
  ExportMenu,
  InvCard,
  ProgressBar,
  ScrollFade,
  SearchField,
  Segmented,
  TableFoot,
  TableScroll,
  TableWrap,
  UpperLabel,
  useElementWidth,
  useMediaQuery,
  numClass,
  tableClass,
  tdClass,
  thClass
} from './InventoryUi';
import {
  BOQ_STATUS_LABEL,
  BOQ_STATUS_TONE,
  EMPTY_FILTER_VALUE,
  availableOf,
  boqCoverage,
  boqStatus,
  bookedForRemaining,
  formatQty,
  formatUnitTotals,
  onHandOf,
  paginate,
  plural,
  sumByUnit,
  toQty
} from './inventoryModel';
import { downloadCsv, downloadXlsx, printTable } from './exportRows';

const STATUS_KEYS = ['all', 'not_booked', 'booked', 'partial', 'delivered'];
const SORTS = [
  { value: 'remaining', label: 'Remaining ↓' },
  { value: 'short', label: 'Short ↓' },
  { value: 'boq', label: 'BOQ qty ↓' },
  { value: 'projects', label: 'Projects ↓' },
  { value: 'part', label: 'Part no. A–Z' }
];
const RAIL_PREVIEW = 7;
const RAIL_WIDTH = 220;
const TABLE_MIN_WIDTH = 800;
/** Rail + gap (16) + the table's minimum width: below this the rail gives way to a Project chip (fits a 1366 laptop). */
const RAIL_LAYOUT_MIN_WIDTH = RAIL_WIDTH + 16 + TABLE_MIN_WIDTH;
/** A project id is never shown as a name: a BOQ line left behind by a deleted project reads like this. */
const DELETED_PROJECT = 'Deleted project';
/** Lines the store can cover first, then the shortages, then what is fully delivered. */
const COVERAGE_ORDER = { covered: 0, short: 1, done: 2 };

const matchesFacet = (value, filter) => {
  if (!filter) return true;
  const clean = String(value || '').trim();
  return filter === EMPTY_FILTER_VALUE ? !clean : clean === filter;
};

function facetOptions(rows, field, emptyLabel) {
  const counts = new Map();
  rows.forEach((row) => {
    const value = String(row[field] || '').trim() || EMPTY_FILTER_VALUE;
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return [...counts.entries()]
    .sort((left, right) => (left[0] === EMPTY_FILTER_VALUE) - (right[0] === EMPTY_FILTER_VALUE) || left[0].localeCompare(right[0]))
    .map(([value, count]) => ({ value, count, label: value === EMPTY_FILTER_VALUE ? emptyLabel : value, muted: value === EMPTY_FILTER_VALUE }));
}

/** Per-line numbers, scoped to one project when a project is picked. */
function scopeLine(item, projectId) {
  if (!projectId) {
    const boq = toQty(item.total_boq_qty);
    const remaining = toQty(item.total_remaining_qty);
    const delivered = item.total_delivered_qty !== undefined && item.total_delivered_qty !== null
      ? toQty(item.total_delivered_qty)
      : (item.project_details || []).reduce((sum, project) => sum + toQty(project.delivered_qty), 0);
    return {
      ...item,
      boq,
      booked: toQty(item.total_booked_qty),
      delivered,
      remaining,
      projectCount: toQty(item.project_count) || (item.project_details || []).length
    };
  }
  const detail = (item.project_details || []).find((project) => project.project_id === projectId);
  if (!detail) return null;
  return {
    ...item,
    total_boq_qty: toQty(detail.boq_qty),
    total_booked_qty: toQty(detail.booked_qty),
    total_delivered_qty: toQty(detail.delivered_qty),
    total_remaining_qty: toQty(detail.remaining_qty),
    project_count: 1,
    project_names: [detail.project_name],
    project_details: [detail],
    boq: toQty(detail.boq_qty),
    booked: toQty(detail.booked_qty),
    delivered: toQty(detail.delivered_qty),
    remaining: toQty(detail.remaining_qty),
    projectCount: 1
  };
}

export default function SitesBoqTab({
  filteredSitesBoq,
  sitesBoq,
  stockItems = [],
  projects = [],
  handleOpenSitesBoqDialog,
  search = '',
  onSearchChange,
  brandFilter = null,
  onBrandFilterChange,
  categoryFilter = null,
  onCategoryFilterChange,
  statusFilter = 'all',
  onStatusFilterChange,
  onReserve,
  onIssue,
  canRequestPurchase = false,
  canEdit = true,
  siteRequests = [],
  onPurchaseRequested,
  projectFilter: projectFilterProp = null,
  onProjectFilterChange,
  canAutoBook = false,
  onAutoBooked
}) {
  const rows = useMemo(() => sitesBoq || filteredSitesBoq || [], [filteredSitesBoq, sitesBoq]);
  // The rail needs room for all 8 columns beside it — measured on the tab itself, so a laptop at
  // 125% scaling (or an open sidebar) switches to the compact Project chip instead of hiding
  // Remaining / Status off to the right.
  const [layoutRef, layoutWidth] = useElementWidth();
  const narrowWindow = useMediaQuery('(max-width: 1023px)');
  const compact = layoutWidth > 0 ? layoutWidth < RAIL_LAYOUT_MIN_WIDTH : narrowWindow;
  // Phones: Item / Remaining / Status only (the rest is in the expansion), so nothing hides off to the right.
  const isPhone = useMediaQuery('(max-width: 639px)');
  // Compact: the Projects column goes (the Project chip scopes instead) so the table fits beside nothing at ~780px.
  const showProjectsCol = !compact && !isPhone;
  const columnCount = isPhone ? 4 : showProjectsCol ? 9 : 8;
  // The project scope can be driven by the page (Dashboard › Site demand opens a project here).
  const [ownProjectFilter, setOwnProjectFilter] = useState(null);
  const projectFilter = onProjectFilterChange ? projectFilterProp : ownProjectFilter;
  const setProjectFilter = onProjectFilterChange || setOwnProjectFilter;
  const [autoBookOpen, setAutoBookOpen] = useState(false);
  const [railQuery, setRailQuery] = useState('');
  const [openGroups, setOpenGroups] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sort, setSort] = useState('remaining');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expandedPart, setExpandedPart] = useState(null);
  const [purchase, setPurchase] = useState(null); // { line, detail }
  const [purchaseAll, setPurchaseAll] = useState(null); // { line, entries: [{ detail, qty }] } — Request purchase for all N

  const projectById = useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects]);
  const stockByPart = useMemo(() => new Map(stockItems.map((item) => [item.part_number, item])), [stockItems]);

  // Projects that have BOQ lines, with their line counts, grouped by system.
  const railGroups = useMemo(() => {
    const lineCounts = new Map();
    const names = new Map();
    rows.forEach((row) => {
      (row.project_details || []).forEach((detail) => {
        lineCounts.set(detail.project_id, (lineCounts.get(detail.project_id) || 0) + 1);
        if (detail.project_name) names.set(detail.project_id, detail.project_name);
      });
    });
    const groups = new Map();
    lineCounts.forEach((count, projectId) => {
      const project = projectById.get(projectId);
      const system = String(project?.system_type || '').trim() || 'Unspecified';
      if (!groups.has(system)) groups.set(system, []);
      const knownName = names.get(projectId) && names.get(projectId) !== projectId ? names.get(projectId) : '';
      groups.get(system).push({ id: projectId, name: project?.name || knownName || DELETED_PROJECT, deleted: !project && !knownName, count, system });
    });
    return [...groups.entries()]
      .map(([system, list]) => ({ system, projects: list.sort((left, right) => right.count - left.count || left.name.localeCompare(right.name)) }))
      .sort((left, right) => (left.system === 'Unspecified') - (right.system === 'Unspecified') || right.projects.length - left.projects.length || left.system.localeCompare(right.system));
  }, [projectById, rows]);

  const projectsWithLines = railGroups.reduce((sum, group) => sum + group.projects.length, 0);
  // Existing projects with no open BOQ line (never counted from ids that are not projects any more).
  const hiddenProjects = useMemo(() => {
    const withLines = new Set(railGroups.flatMap((group) => group.projects.map((project) => project.id)));
    return projects.filter((project) => !withLines.has(project.id)).length;
  }, [projects, railGroups]);
  const isGroupOpen = (system, index) => (openGroups ? openGroups.has(system) : index < 2);
  const toggleGroup = (system, index) => {
    setOpenGroups((current) => {
      const next = new Set(current || railGroups.filter((_, groupIndex) => groupIndex < 2).map((group) => group.system));
      if (isGroupOpen(system, index)) next.delete(system); else next.add(system);
      return next;
    });
  };

  const selectedProject = projectFilter
    ? railGroups.flatMap((group) => group.projects).find((project) => project.id === projectFilter)
    : null;

  // Search + brand/category + project scope (the strip and the status counts follow these).
  const scoped = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows
      .filter((item) => !query || [item.part_number, item.description].filter(Boolean).join(' ').toLowerCase().includes(query))
      .filter((item) => matchesFacet(item.brand, brandFilter) && matchesFacet(item.category, categoryFilter))
      .map((item) => scopeLine(item, projectFilter))
      .filter(Boolean)
      .map((line) => {
        const stockItem = stockByPart.get(line.part_number);
        const available = stockItem ? availableOf(stockItem) : 0;
        const onHand = stockItem ? Math.max(0, onHandOf(stockItem)) : 0;
        // Stock booked for these projects is theirs to issue: it covers the line as much as free stock.
        const coverage = boqCoverage(line.remaining, available, bookedForRemaining(line.project_details), onHand);
        return { ...line, status: boqStatus(line), available, onHand, coverage };
      });
  }, [brandFilter, categoryFilter, projectFilter, rows, search, stockByPart]);

  const statusCounts = useMemo(() => Object.fromEntries(STATUS_KEYS.map((key) => [
    key,
    key === 'all' ? scoped.length : scoped.filter((line) => line.status === key).length
  ])), [scoped]);

  const visible = useMemo(() => {
    const list = statusFilter === 'all' ? scoped : scoped.filter((line) => line.status === statusFilter);
    const sorted = [...list];
    sorted.sort((left, right) => {
      const group = COVERAGE_ORDER[left.coverage.state] - COVERAGE_ORDER[right.coverage.state];
      if (group) return group;
      if (sort === 'part') return String(left.part_number).localeCompare(String(right.part_number));
      if (sort === 'boq') return right.boq - left.boq || String(left.part_number).localeCompare(String(right.part_number));
      if (sort === 'projects') return right.projectCount - left.projectCount || right.remaining - left.remaining;
      if (sort === 'short') return right.coverage.short - left.coverage.short || right.remaining - left.remaining || String(left.part_number).localeCompare(String(right.part_number));
      return right.remaining - left.remaining || String(left.part_number).localeCompare(String(right.part_number));
    });
    return sorted;
  }, [scoped, sort, statusFilter]);

  useEffect(() => { setPage(1); }, [search, brandFilter, categoryFilter, projectFilter, statusFilter, sort, pageSize]);

  const strip = useMemo(() => {
    const fractions = scoped.filter((line) => line.boq > 0).map((line) => Math.min(1, line.delivered / line.boq));
    const deliveredPct = fractions.length ? (fractions.reduce((sum, value) => sum + value, 0) / fractions.length) * 100 : 0;
    const projectIds = new Set();
    scoped.forEach((line) => (line.project_details || []).forEach((detail) => projectIds.add(detail.project_id)));
    return {
      lines: scoped.length,
      projects: projectIds.size,
      deliveredPct,
      booked: sumByUnit(scoped, (line) => line.booked),
      remaining: sumByUnit(scoped, (line) => line.remaining),
      short: scoped.filter((line) => line.coverage.state === 'short').length,
      covered: scoped.filter((line) => line.coverage.state === 'covered').length,
      toBuy: sumByUnit(scoped.filter((line) => line.coverage.state === 'short'), (line) => line.coverage.short)
    };
  }, [scoped]);
  const groupCounts = useMemo(() => ({
    covered: visible.filter((line) => line.coverage.state === 'covered').length,
    short: visible.filter((line) => line.coverage.state === 'short').length,
    done: visible.filter((line) => line.coverage.state === 'done').length
  }), [visible]);
  const groupToBuy = useMemo(() => sumByUnit(visible.filter((line) => line.coverage.state === 'short'), (line) => line.coverage.short), [visible]);

  const paged = paginate(visible, page, pageSize);
  const brandOptions = useMemo(() => facetOptions(rows, 'brand', 'No brand'), [rows]);
  const categoryOptions = useMemo(() => facetOptions(rows, 'category', 'No category'), [rows]);
  const projectChipOptions = useMemo(() => railGroups.flatMap((group) => group.projects.map((project) => ({
    value: project.id,
    label: `${project.name} · ${group.system}`,
    count: project.count
  }))), [railGroups]);

  const exportColumns = [
    { header: 'Part number', nowrap: true, value: (line) => line.part_number },
    { header: 'Description', value: (line) => line.description || '' },
    { header: 'Brand', value: (line) => line.brand || '' },
    { header: 'Category', value: (line) => line.category || '' },
    { header: 'Unit', value: (line) => line.unit || 'pcs' },
    ...(projectFilter ? [] : [{ header: 'Projects', numeric: true, value: (line) => line.projectCount }]),
    { header: 'BOQ', numeric: true, value: (line) => line.boq },
    { header: 'Booked', numeric: true, value: (line) => line.booked },
    { header: 'Delivered', numeric: true, value: (line) => line.delivered },
    { header: 'Remaining', numeric: true, value: (line) => line.remaining },
    { header: 'In stock', numeric: true, value: (line) => line.available },
    { header: 'Short vs stock', numeric: true, value: (line) => line.coverage.short },
    { header: 'Status', nowrap: true, value: (line) => BOQ_STATUS_LABEL[line.status] }
  ];
  const exportName = selectedProject ? `sites-boq-${selectedProject.name}` : 'sites-boq-all-projects';
  const exportTitle = selectedProject ? `Sites BOQ — ${selectedProject.name}` : 'Sites BOQ — all projects';

  const pickProject = (projectId) => setProjectFilter(projectFilter === projectId ? null : projectId);
  const hasFilters = Boolean(search || brandFilter || categoryFilter || statusFilter !== 'all');

  const railNeedle = railQuery.trim().toLowerCase();

  return (
    <div ref={layoutRef} className="space-y-4" data-testid="inventory-sites-boq-tab">
      {/* Summary strip */}
      <InvCard data-testid="sites-boq-summary">
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3.5 sm:flex sm:flex-wrap sm:items-stretch sm:gap-x-0 sm:px-5">
        <StripStat label="BOQ lines" value={formatNumber(strip.lines)} testId="sites-boq-lines" />
        <StripStat label="Projects" value={formatNumber(strip.projects)} unit={projectFilter ? undefined : `of ${formatNumber(projects.length || strip.projects)}`} />
        <StripStat label="Delivered" value={formatPercent(strip.deliveredPct)}>
          <ProgressBar value={strip.deliveredPct} tone="green" className="w-[56px] xl:w-[70px]" label="Delivered" />
        </StripStat>
        <UnitStat label="Booked" totals={strip.booked} />
        <UnitStat label="Remaining" totals={strip.remaining} testId="sites-boq-remaining" />
        <StripStat label="Short vs stock" value={formatNumber(strip.short)} unit={strip.short === 1 ? 'line' : 'lines'} tone={strip.short ? 'red' : undefined} last />
        {canAutoBook ? (
          <div className="col-span-2 flex items-center sm:ml-auto sm:pl-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-ink"
              aria-expanded={autoBookOpen}
              onClick={() => setAutoBookOpen((open) => !open)}
              data-testid="sites-boq-auto-book"
              title={selectedProject ? `Book free stock for ${selectedProject.name}` : 'Book free stock for every project with open BOQ lines'}
            >
              <WandSparkles aria-hidden="true" /> Auto-book stock
              {autoBookOpen ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
            </Button>
          </div>
        ) : null}
      </div>
      {canAutoBook && autoBookOpen ? (
        <AutoBookPanel
          key={projectFilter || 'all'}
          projectId={projectFilter}
          projectName={selectedProject?.name || ''}
          canSendShortage={canRequestPurchase}
          onApplied={onAutoBooked}
          onClose={() => setAutoBookOpen(false)}
        />
      ) : null}
      </InvCard>

      {/* Toolbar: one line on a wide screen (the status control may scroll inside itself); it wraps below that */}
      <div className="flex flex-wrap items-center gap-2 min-[1400px]:flex-nowrap">
        <SearchField value={search} onChange={onSearchChange} placeholder="Part no. or description…" className="w-full shrink-0 sm:w-[220px] xl:w-[240px]" label="Search BOQ lines by part no. or description" />
        {compact ? (
          <ChipSelect label="Project" value={projectFilter} options={projectChipOptions} onChange={setProjectFilter} testId="sites-boq-chip-project" />
        ) : null}
        <ChipSelect label="Brand" value={brandFilter} options={brandOptions} onChange={onBrandFilterChange} testId="sites-boq-chip-brand" />
        <ChipSelect label="Category" value={categoryFilter} options={categoryOptions} onChange={onCategoryFilterChange} testId="sites-boq-chip-category" />
        <ScrollFade className="w-full sm:w-auto xl:ml-auto min-[1400px]:shrink" testId="sites-boq-status-scroll">
          <Segmented
            className="max-w-none"
            ariaLabel="BOQ line status"
            value={statusFilter}
            onChange={onStatusFilterChange}
            options={STATUS_KEYS.map((key) => ({
              key,
              label: key === 'all' ? 'All' : BOQ_STATUS_LABEL[key],
              count: statusCounts[key],
              dot: key === 'all' ? undefined : BOQ_STATUS_TONE[key],
              testId: `sites-boq-status-${key}`
            }))}
          />
        </ScrollFade>
        <label className="flex h-[30px] shrink-0 items-center rounded-lg border border-line bg-surface pl-1.5 pr-1 text-[12.5px] font-semibold text-ink-2 shadow-card">
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            className="h-full bg-transparent pr-1 text-ink outline-none"
            data-testid="sites-boq-sort"
            aria-label="Sort BOQ lines"
          >
            {SORTS.map((option) => <option key={option.value} value={option.value}>Sort: {option.label}</option>)}
          </select>
        </label>
        <ExportMenu
          iconOnly
          label="Export these BOQ lines"
          testId="sites-boq-export-btn"
          disabled={!visible.length}
          onExcel={() => downloadXlsx({ filename: exportName, sheetName: 'Sites BOQ', columns: exportColumns, rows: visible })}
          onCsv={() => downloadCsv({ filename: exportName, columns: exportColumns, rows: visible })}
          onPdf={() => printTable({
            title: exportTitle,
            subtitle: `${plural(visible.length, 'BOQ line')} · ${statusFilter === 'all' ? 'all statuses' : BOQ_STATUS_LABEL[statusFilter]} · exported ${formatDate(new Date().toISOString())}`,
            columns: exportColumns,
            rows: visible
          })}
        />
      </div>

      <div className={cn('grid items-start gap-4', !compact && 'grid-cols-[220px_minmax(0,1fr)]')}>
        {/* Project rail (a Project chip in the toolbar replaces it on narrow screens) */}
        {compact ? null : (
        <InvCard className="p-3" data-testid="sites-boq-rail">
          <label className="flex h-8 items-center gap-2 rounded-lg border border-line bg-surface px-2.5 text-ink-4 focus-within:border-[color:var(--brand-ink)]">
            <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="sr-only">Find project</span>
            <input
              value={railQuery}
              onChange={(event) => setRailQuery(event.target.value)}
              placeholder="Find project…"
              className="h-full min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-4"
              data-testid="sites-boq-rail-search"
            />
          </label>
          <div className="mt-2 flex items-center justify-between px-2 py-1">
            <UpperLabel>Project</UpperLabel>
            <UpperLabel>BOQ lines</UpperLabel>
          </div>
          <RailRow
            icon={Layers}
            label={`All ${formatNumber(projectsWithLines)} projects`}
            count={rows.length}
            active={!projectFilter}
            onClick={() => setProjectFilter(null)}
          />
          <div className="mt-1 space-y-1">
            {railGroups.map((group, index) => {
              const matching = railNeedle ? group.projects.filter((project) => project.name.toLowerCase().includes(railNeedle)) : group.projects;
              if (railNeedle && !matching.length) return null;
              const open = railNeedle ? true : isGroupOpen(group.system, index);
              const showAll = railNeedle || expandedGroups[group.system];
              const shown = showAll ? matching : matching.slice(0, RAIL_PREVIEW);
              return (
                <div key={group.system} role="group" aria-label={`${group.system} projects`} className="border-t border-line-2 pt-1 first:border-t-0">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.system, index)}
                    aria-expanded={open}
                    className="flex h-8 w-full items-center gap-1.5 rounded-lg px-2 text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {open ? <ChevronDown className="h-3 w-3 text-ink-3" aria-hidden="true" /> : <ChevronRight className="h-3 w-3 text-ink-3" aria-hidden="true" />}
                    <span className="flex-1 truncate text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-2">{group.system}</span>
                    <span className="text-[11.5px] text-ink-3">{plural(group.projects.length, 'project')}</span>
                  </button>
                  {open ? (
                    <div className="pb-1">
                      {shown.map((project) => (
                        <RailRow
                          key={project.id}
                          label={project.name}
                          count={project.count}
                          active={projectFilter === project.id}
                          onClick={() => pickProject(project.id)}
                          muted={project.deleted}
                          indent
                        />
                      ))}
                      {!showAll && matching.length > RAIL_PREVIEW ? (
                        <button
                          type="button"
                          onClick={() => setExpandedGroups((current) => ({ ...current, [group.system]: true }))}
                          className="ml-7 mt-0.5 text-[12px] font-semibold text-brand-ink hover:underline"
                        >
                          + {matching.length - RAIL_PREVIEW} more
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {hiddenProjects > 0 ? (
            <p className="mt-2 flex items-center gap-1.5 border-t border-line-2 px-2 pt-2.5 text-[11.5px] text-ink-3">
              <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> {plural(hiddenProjects, 'project')} with no BOQ hidden
            </p>
          ) : null}
        </InvCard>
        )}

        {/* BOQ lines */}
        <TableWrap data-testid="sites-boq-table">
          {selectedProject ? (
            <div className="flex flex-wrap items-center gap-2 border-b border-line-2 px-4 py-2.5 text-[12.5px] text-ink-3">
              Showing <b className="font-semibold text-ink">{selectedProject.name}</b> · {selectedProject.system}
              <button type="button" onClick={() => setProjectFilter(null)} className="font-semibold text-brand-ink hover:underline">Show all projects</button>
            </div>
          ) : null}
          <TableScroll>
            <table className={cn(tableClass, 'table-fixed', isPhone ? 'min-w-0' : showProjectsCol ? 'min-w-[800px]' : 'min-w-[740px]')}>
              <colgroup>
                <col />
                {showProjectsCol ? <col className="w-[66px]" /> : null}
                {isPhone ? null : <col className="w-[60px]" />}
                {isPhone ? null : <col className="w-[66px]" />}
                {isPhone ? null : <col className="w-[76px]" />}
                <col className={isPhone ? 'w-[84px]' : 'w-[88px]'} />
                {isPhone ? null : <col className="w-[92px]" />}
                <col className={isPhone ? 'w-[104px]' : 'w-[124px]'} />
                <col className="w-9" />
              </colgroup>
              <thead>
                <tr>
                  <th className={thClass}>Item</th>
                  {showProjectsCol ? <th className={cn(thClass, numClass)}>Projects</th> : null}
                  {isPhone ? null : <th className={cn(thClass, numClass)}>BOQ</th>}
                  {isPhone ? null : <th className={cn(thClass, numClass)}>Booked</th>}
                  {isPhone ? null : <th className={cn(thClass, numClass)}>Delivered</th>}
                  <th
                    className={cn(thClass, numClass, sort === 'remaining' && 'text-ink')}
                    title="Counted per project: BOQ − delivered for each project (a project that got more than its BOQ does not lower the others)"
                  >
                    Remaining{sort === 'remaining' ? ' ↓' : ''}
                  </th>
                  {isPhone ? null : (
                    <th className={cn(thClass, numClass, sort === 'short' && 'text-ink')} title="Free stock in any store. Covered = free stock plus what is already booked for these projects covers the remaining BOQ">
                      In stock{sort === 'short' ? ' ↓' : ''}
                    </th>
                  )}
                  <th className={thClass}>Status</th>
                  <th className={thClass}><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((line, index) => {
                  const unit = String(line.unit || 'pcs').trim() || 'pcs';
                  const unitSuffix = unit !== 'pcs' ? <span className="ml-0.5 text-[11px] font-medium text-ink-3">{unit}</span> : null;
                  const noCategory = !String(line.category || '').trim();
                  const open = expandedPart === line.part_number;
                  const previous = paged.rows[index - 1];
                  const startsGroup = !previous || previous.coverage.state !== line.coverage.state;
                  const continued = index === 0 && paged.start > 0 && visible[paged.start - 1]?.coverage.state === line.coverage.state;
                  const bookable = Math.max(0, Math.min(line.available, line.remaining - line.booked));
                  return (
                    <Fragment key={line.part_number}>
                      {startsGroup ? (
                        <CoverageHeader state={line.coverage.state} count={groupCounts[line.coverage.state]} toBuy={groupToBuy} continued={continued} colSpan={columnCount} />
                      ) : null}
                      {/* The whole row opens the projects for mouse users; keyboard users get the button in the last cell. */}
                      <tr
                        onClick={() => setExpandedPart(open ? null : line.part_number)}
                        className={cn('group cursor-pointer', open ? '[&>td]:bg-brand-soft [&>td:first-child]:shadow-[inset_3px_0_0_var(--brand)]' : '[&:focus-within>td]:bg-brand-soft [&:hover>td]:bg-surface-hover')}
                        data-testid={`sites-boq-row-${line.part_number}`}
                      >
                        <td className={cn(tdClass, 'py-1.5')}>
                          <span className="flex min-w-0 items-baseline gap-2">
                            <span className="shrink-0 font-mono text-[12.5px] text-ink">{line.part_number}</span>
                            <span className="truncate text-[11px] text-ink-3">
                              {line.brand || 'No brand'} · {noCategory ? <span className="text-status-amber-ink">No category</span> : line.category}
                            </span>
                          </span>
                          <span className="mt-px block truncate text-[12px] text-ink-2" title={line.description}>{line.description || '—'}</span>
                        </td>
                        {showProjectsCol ? <td className={cn(tdClass, numClass, 'text-ink-2')}>{formatNumber(line.projectCount)}</td> : null}
                        {isPhone ? null : <td className={cn(tdClass, numClass, 'text-ink-2')}>{formatQty(line.boq)}{unitSuffix}</td>}
                        {isPhone ? null : <td className={cn(tdClass, numClass, line.booked > 0 ? 'font-semibold text-status-blue-ink' : 'text-ink-3')}>{formatQty(line.booked)}</td>}
                        {isPhone ? null : (
                          <td className={cn(tdClass, numClass, line.delivered > 0 ? 'text-ink' : 'text-ink-3')} data-testid={`sites-boq-delivered-${line.part_number}`}>
                            {formatQty(line.delivered)}
                          </td>
                        )}
                        <td
                          className={cn(tdClass, numClass, 'font-bold', line.remaining > 0 ? 'text-ink' : 'text-ink-3')}
                          title={overDelivered(line) > 0 ? `Remaining is counted per project — ${formatQty(overDelivered(line))} delivered over a project's BOQ does not lower what the other projects still need` : undefined}
                          data-testid={`sites-boq-remaining-${line.part_number}`}
                        >
                          {formatQty(line.remaining)}{line.remaining > 0 ? unitSuffix : null}
                          {isPhone && line.coverage.state === 'short' ? <span className="block text-[11.5px] font-semibold text-status-red-ink">Short {formatQty(line.coverage.short)}</span> : null}
                        </td>
                        {isPhone ? null : (
                          <td
                            className={cn(tdClass, numClass, 'py-1.5')}
                            title={line.coverage.booked > 0 ? `${formatQty(line.available)} free + ${formatQty(line.coverage.booked)} booked for these projects` : undefined}
                            data-testid={`sites-boq-instock-${line.part_number}`}
                          >
                            <span className={cn('block', line.available > 0 ? 'font-semibold text-ink' : 'text-ink-3')}>{formatQty(line.available)}</span>
                            {line.coverage.state === 'covered' ? <span className="block text-[11.5px] font-semibold text-status-green-ink">Covered</span> : null}
                            {line.coverage.state === 'short' ? <span className="block text-[11.5px] font-semibold text-status-red-ink">Short {formatQty(line.coverage.short)}</span> : null}
                          </td>
                        )}
                        <td className={cn(tdClass, 'py-1.5')}>
                          <StatusPill tone={BOQ_STATUS_TONE[line.status]} label={BOQ_STATUS_LABEL[line.status]} data-testid={`sites-boq-status-pill-${line.part_number}`} />
                          {canEdit && bookable > 0 && line.status !== 'booked' ? (
                            <button
                              type="button"
                              onClick={(event) => { event.stopPropagation(); onReserve?.(line, (line.project_details || []).length === 1 ? line.project_details[0] : null, bookable); }}
                              className="mt-0.5 block text-[11.5px] font-semibold text-brand-ink hover:underline"
                              data-testid={`sites-boq-book-${line.part_number}`}
                            >
                              Book {formatQty(bookable)} →
                            </button>
                          ) : null}
                        </td>
                        <td className={cn(tdClass, 'pl-0 pr-2 text-right')}>
                          <button
                            type="button"
                            onClick={(event) => { event.stopPropagation(); setExpandedPart(open ? null : line.part_number); }}
                            aria-label={`${open ? 'Hide' : 'Show'} projects using ${line.part_number}`}
                            aria-expanded={open}
                            className="ml-auto grid h-7 w-7 place-items-center rounded-md text-ink-4 hover:bg-surface-hover hover:text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:text-ink-3"
                            data-testid={`sites-boq-open-${line.part_number}`}
                          >
                            {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                          </button>
                        </td>
                      </tr>
                      {open ? (
                        <tr data-testid={`sites-boq-expansion-${line.part_number}`}>
                          <td colSpan={columnCount} className="border-b border-line-2 bg-surface-2 px-4 pb-3 pt-2.5 shadow-[inset_3px_0_0_var(--brand)]">
                            <ProjectBreakdown
                              line={line}
                              projectById={projectById}
                              canRequestPurchase={canRequestPurchase}
                              canEdit={canEdit}
                              siteRequests={siteRequests}
                              onReserve={(detail, qty) => onReserve?.(line, detail, qty)}
                              onIssue={(detail) => onIssue?.(line, detail)}
                              onRequestPurchase={(detail, existing) => setPurchase({ line, detail, existing })}
                              onRequestForProjects={(entries) => setPurchaseAll({ line, entries })}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
          {!paged.rows.length ? (
            <EmptyState
              icon={hasFilters ? SearchX : PackageSearch}
              title="No BOQ items found"
              text={hasFilters ? 'Nothing matches these filters for this project scope.' : 'This project has no BOQ lines with part numbers yet.'}
              action={hasFilters ? (
                <Button size="sm" variant="outline" className="text-ink" onClick={() => { onSearchChange?.(''); onBrandFilterChange?.(null); onCategoryFilterChange?.(null); onStatusFilterChange?.('all'); }}>
                  Clear filters
                </Button>
              ) : null}
            />
          ) : null}
          <TableFoot
            left={(
              <span>
                {visible.length ? `${formatNumber(paged.start + 1)}–${formatNumber(paged.end)} of ` : '0 of '}
                <b className="font-semibold text-ink-2">{formatNumber(visible.length)}</b> · grouped by stock coverage · <span data-testid="sites-boq-sorted-by">sorted by {SORTS.find((option) => option.value === sort)?.label.replace(/ [↓A].*$/, '').toLowerCase()}</span>
              </span>
            )}
            page={paged.page}
            pageCount={paged.pageCount}
            onPage={setPage}
            pageSize={pageSize}
            onPageSize={setPageSize}
          />
        </TableWrap>
      </div>
      <RequestPurchaseDialog
        open={Boolean(purchase)}
        onOpenChange={(next) => { if (!next) setPurchase(null); }}
        line={purchase?.line}
        detail={purchase?.detail}
        existing={purchase?.existing}
        onRequested={() => { setPurchase(null); onPurchaseRequested?.(); }}
      />
      <RequestForProjectsDialog
        open={Boolean(purchaseAll)}
        onOpenChange={(next) => { if (!next) setPurchaseAll(null); }}
        line={purchaseAll?.line}
        entries={purchaseAll?.entries || []}
        onRequested={() => { setPurchaseAll(null); onPurchaseRequested?.(); }}
      />
    </div>
  );
}

/** What was delivered over a project's BOQ, summed over the line's projects (remaining is clamped per project). */
function overDelivered(line) {
  return (line.project_details || []).reduce(
    (sum, detail) => sum + Math.max(0, toQty(detail.delivered_qty) - toQty(detail.boq_qty)),
    0
  );
}

function CoverageHeader({ state, count, toBuy, continued, colSpan = 9 }) {
  const config = {
    covered: { icon: CheckCircle2, tone: 'text-status-green-ink', title: 'Covered by stock', right: 'can be booked now' },
    short: { icon: CircleAlert, tone: 'text-status-red-ink', title: 'Short vs stock', right: `${formatUnitTotals(toBuy)} to buy` },
    done: { icon: CheckCircle2, tone: 'text-ink-3', title: 'Fully delivered', right: '' }
  }[state];
  const Icon = config.icon;
  return (
    <tr data-testid={`sites-boq-group-${state}`}>
      <td colSpan={colSpan} className="h-9 border-b border-line-2 bg-surface px-4">
        <span className="flex items-center gap-2 text-[12.5px]">
          <Icon className={cn('h-3.5 w-3.5', config.tone)} aria-hidden="true" />
          <b className="font-bold text-ink">{config.title}</b>
          <span className="text-ink-3">· {plural(count, 'line')}{continued ? ' (continued)' : ''}</span>
          {config.right ? <span className="ml-auto truncate pl-2 text-[12px] text-ink-3">{config.right}</span> : null}
        </span>
      </td>
    </tr>
  );
}

/** Open purchase requests (Procurement › Site requests) of one project for this line. */
function openRequestsFor(siteRequests, line, detail) {
  const boqIds = new Set([...(detail.boq_item_ids || []), detail.boq_item_id].filter(Boolean));
  return siteRequests.filter((request) => (
    request.project_id === detail.project_id
    && (request.part_number === line.part_number || (request.boq_item_id && boqIds.has(request.boq_item_id)))
    && toQty(request.remaining_qty ?? request.requested_qty) > 0
  ));
}

/**
 * The expansion of a BOQ line (Sites BOQ idea 5): each project's numbers and what can be done for it.
 * Stock already booked for a project is that project's to issue: Issue DN stays available for it and
 * it is never asked to buy what it holds. Reserve uses free stock only.
 */
function ProjectBreakdown({ line, projectById, canRequestPurchase, canEdit = true, siteRequests = [], onReserve, onIssue, onRequestPurchase, onRequestForProjects }) {
  const free = Math.max(0, toQty(line.available));
  const onHand = Math.max(0, toQty(line.onHand));
  const rows = [...(line.project_details || [])]
    .sort((left, right) => toQty(right.remaining_qty) - toQty(left.remaining_qty))
    .map((detail) => {
      const remaining = toQty(detail.remaining_qty);
      const booked = Math.max(0, toQty(detail.booked_qty));
      const open = Math.max(0, remaining - booked);
      const requests = openRequestsFor(siteRequests, line, detail);
      const requestedQty = requests.reduce((sum, request) => sum + toQty(request.remaining_qty ?? request.requested_qty), 0);
      return {
        detail,
        remaining,
        booked,
        open,
        requests,
        requestedQty,
        firstDue: requests.map((request) => request.due_date).filter(Boolean).sort()[0],
        stillToRequest: Math.max(0, open - requestedQty),
        // Delivered more than this project's BOQ (its remaining is 0, the extra does not lower the others).
        over: Math.max(0, toQty(detail.delivered_qty) - toQty(detail.boq_qty))
      };
    });
  const bookedTotal = rows.reduce((sum, row) => sum + toQty(row.detail.booked_qty), 0);
  // Nothing free in the stores and two or more projects still to ask for: one request for all of them.
  const requestable = rows.filter((row) => row.stillToRequest > 0 && row.detail.boq_item_id);
  const canRequestAll = canRequestPurchase && free <= 0 && requestable.length >= 2;
  return (
    <div className="sm:pl-7">
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12.5px]">
        <b className="font-bold text-ink">Needed by {plural(rows.length, 'project')}</b>
        <span className="text-ink-3">
          · {formatQty(line.remaining)} {line.unit || 'pcs'} remaining · {formatQty(free)} free in stock
          {bookedTotal > 0 ? ` · ${formatQty(bookedTotal)} booked` : ''}
        </span>
        {canRequestAll ? (
          <Button
            type="button"
            size="xs"
            variant="outline"
            className="ml-auto text-ink"
            onClick={() => onRequestForProjects?.(requestable.map((row) => ({ detail: row.detail, qty: row.stillToRequest })))}
            data-testid={`sites-boq-request-all-${line.part_number}`}
          >
            <ShoppingCart aria-hidden="true" /> Request purchase for all {requestable.length}
          </Button>
        ) : null}
      </div>
      <div className="overflow-x-auto">
      <table className="w-full min-w-[620px] table-fixed border-separate border-spacing-0 text-[12.5px]">
        <colgroup>
          <col />
          <col className="w-[64px]" />
          <col className="w-[70px]" />
          <col className="w-[76px]" />
          <col className="w-[84px]" />
          <col className="w-[260px]" />
        </colgroup>
        <thead>
          <tr className="text-[11px] text-ink-3">
            <th className="h-8 border-b border-line text-left font-bold">Project</th>
            <th className="h-8 border-b border-line text-right font-bold">BOQ</th>
            <th className="h-8 border-b border-line text-right font-bold">Booked</th>
            <th className="h-8 border-b border-line text-right font-bold">Delivered</th>
            <th className="h-8 border-b border-line text-right font-bold">Remaining</th>
            <th className="h-8 border-b border-line"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ detail, remaining, booked, open, requests, requestedQty, firstDue, stillToRequest, over }) => {
            const system = projectById.get(detail.project_id)?.system_type;
            // What a Site DN for this project can take: free stock plus its own booking (if still on hand).
            const issuable = free + Math.min(booked, onHand);
            const issueBlocked = !canEdit ? 'Your role can view stock but not issue it' : remaining <= 0 ? 'Nothing left to deliver' : issuable <= 0 ? 'Nothing in stock to issue' : undefined;
            return (
              <tr key={detail.project_id} data-testid={`sites-boq-project-${line.part_number}-${detail.project_id}`}>
                <td className="h-9 border-b border-line-2 py-1 pr-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-semibold text-ink">{detail.project_name}</span>
                    {system ? <span className="shrink-0 rounded border border-line bg-surface px-1 text-[10.5px] font-bold text-ink-2">{system}</span> : null}
                  </span>
                  {requestedQty > 0 ? (
                    <span
                      className="mt-px flex items-center gap-1 whitespace-nowrap text-[11.5px] font-semibold text-status-blue-ink"
                      title={`${plural(requests.length, 'open purchase request')} on Procurement › Site requests`}
                      data-testid={`sites-boq-requested-${detail.project_id}`}
                    >
                      <ShoppingCart className="h-3 w-3 shrink-0" aria-hidden="true" />
                      Requested {formatQty(requestedQty)}{firstDue ? ` · due ${formatDate(firstDue, { year: 'auto' })}` : ''}
                    </span>
                  ) : null}
                </td>
                <td className="h-9 border-b border-line-2 text-right tabular-nums text-ink-2">{formatQty(detail.boq_qty)}</td>
                <td className={cn('h-9 border-b border-line-2 text-right tabular-nums', booked > 0 ? 'font-semibold text-status-blue-ink' : 'text-ink-3')}>{formatQty(detail.booked_qty)}</td>
                <td className={cn('h-9 border-b border-line-2 text-right tabular-nums', over > 0 ? 'font-semibold text-status-amber-ink' : 'text-ink-3')}>
                  {formatQty(detail.delivered_qty)}
                  {over > 0 ? (
                    <span className="block whitespace-nowrap text-[11px]" title={`Delivered ${formatQty(over)} more than this project's BOQ (${formatQty(detail.boq_qty)})`} data-testid={`sites-boq-over-${detail.project_id}`}>
                      {formatQty(over)} over BOQ
                    </span>
                  ) : null}
                </td>
                <td className="h-9 border-b border-line-2 text-right font-bold tabular-nums text-ink">{formatQty(remaining)}</td>
                <td className="h-9 border-b border-line-2 pl-3">
                  <span className="flex items-center justify-end gap-1.5">
                    {canEdit && free > 0 && open > 0 ? (
                      <Button type="button" size="xs" variant="outline" className="text-ink" onClick={() => onReserve(detail, Math.min(open, free))} data-testid={`sites-boq-reserve-${detail.project_id}`}>
                        <Lock aria-hidden="true" /> Reserve
                      </Button>
                    ) : null}
                    {free <= 0 && stillToRequest > 0 ? (
                      <span title={!canRequestPurchase ? 'Needs access to Material' : !detail.boq_item_id ? 'This BOQ line has no id — open it in the project' : undefined} className="inline-flex">
                        <Button
                          type="button"
                          size="xs"
                          variant="outline"
                          className="text-ink"
                          disabled={!canRequestPurchase || !detail.boq_item_id}
                          onClick={() => onRequestPurchase(detail, requestedQty > 0 ? { qty: requestedQty, due: firstDue, count: requests.length } : null)}
                          data-testid={`sites-boq-request-${detail.project_id}`}
                        >
                          <ShoppingCart aria-hidden="true" /> {requestedQty > 0 ? 'Request more' : 'Request purchase'}
                        </Button>
                      </span>
                    ) : null}
                    <span title={issueBlocked} className="inline-flex">
                      <Button type="button" size="xs" variant="ghost" className="text-ink-2" disabled={Boolean(issueBlocked)} onClick={() => onIssue(detail)} data-testid={`sites-boq-issue-${detail.project_id}`}>
                        <Truck aria-hidden="true" /> Issue DN
                      </Button>
                    </span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-3">
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {free > 0
          ? `${formatQty(free)} free in the stores · Reserve keeps them for a project, Issue DN sends them${bookedTotal > 0 ? ' · booked stock is issued to its own project' : ''}.`
          : bookedTotal > 0 && onHand > 0
            ? `No free stock · what is booked can be issued to its own project. Request purchase adds the rest to Procurement › Site requests.`
            : `Reserve and Issue DN appear once stock is available at any location · 0 of ${formatQty(line.remaining)} in stock today. Request purchase adds it to Procurement › Site requests.`}
      </p>
    </div>
  );
}


/** One number per unit — pieces first, other units ('+ 12 m') beside it, never added together. */
function UnitStat({ label, totals, testId }) {
  const entries = Object.entries(totals || {}).filter(([, qty]) => toQty(qty) > 0);
  const [mainUnit, mainQty] = entries.find(([unit]) => unit === 'pcs') || entries[0] || ['pcs', 0];
  const others = entries.filter(([unit]) => unit !== mainUnit);
  return (
    <StripStat
      label={label}
      value={formatQty(mainQty)}
      unit={mainUnit}
      testId={testId}
      below={others.length ? (
        <span className="block truncate text-[12px] text-ink-3" title={formatUnitTotals(totals)}>
          + {others.map(([unit, qty]) => `${formatQty(qty)} ${unit}`).join(' · ')}
        </span>
      ) : null}
    />
  );
}

function StripStat({ label, value, unit, tone, children, below, last, testId }) {
  return (
    <div className={cn('flex flex-col justify-center gap-1 sm:min-w-[104px] sm:flex-1 sm:px-3 sm:first:pl-0 xl:px-4', !last && 'sm:border-r sm:border-line-2')}>
      <UpperLabel className="whitespace-nowrap">{label}</UpperLabel>
      <div className="flex items-center gap-2">
        <span className={cn('font-heading text-[22px] font-black leading-none tabular-nums', tone === 'red' ? 'text-status-red-ink' : 'text-ink')} data-testid={testId}>
          {value}
        </span>
        {unit ? <span className={cn('text-[12.5px] font-semibold', tone === 'red' ? 'text-status-red-ink' : 'text-ink-3')}>{unit}</span> : null}
        {children}
      </div>
      {below}
    </div>
  );
}

function RailRow({ icon: Icon, label, count, active, onClick, indent, muted = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex h-8 w-full items-center gap-2 rounded-lg px-2 text-left text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        indent && 'pl-7',
        active ? 'bg-brand-soft font-semibold text-brand-ink' : muted ? 'italic text-ink-3 hover:bg-surface-hover' : 'text-ink hover:bg-surface-hover'
      )}
    >
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className={cn('rounded-full px-1.5 text-[11.5px] font-semibold tabular-nums', active ? 'bg-brand text-white' : 'bg-[color:var(--status-gray-bg)] text-ink-3')}>
        {formatNumber(count)}
      </span>
    </button>
  );
}
